import { create } from "zustand"

import {
  type PasskeyLoginFailureType,
  classifyPasskeyLoginFailure,
  isActionablePasskeyFailure,
} from "@src/features/webauthn/lib/classifyPasskeyLoginFailure"
import { useLastConnectedWalletStore } from "@src/stores/useLastConnectedWalletStore"
import { MAX_ERROR_MESSAGE_LENGTH } from "@src/utils/classifyWalletConnectionFailure"
import { toError } from "@src/utils/errors"
import { logger } from "@src/utils/logger"
import { trackEvent } from "@src/utils/trackEvent"
import { useWebAuthnStore } from "./useWebAuthnStore"

const SIGN_IN_CREDENTIAL_NOT_FOUND_MESSAGE =
  "This passkey isn’t registered with this app. Create a new passkey below or use another sign-in method."

function getPasskeyLoginErrorMessage(
  failureType: PasskeyLoginFailureType,
  rawMessage: string
) {
  if (failureType === "credential_not_found") {
    return SIGN_IN_CREDENTIAL_NOT_FOUND_MESSAGE
  }

  return rawMessage
}

type State = {
  isOpen: boolean
  isCreating: boolean
  isSigningIn: boolean
  createError: string | null
  signInError: string | null
}

type Actions = {
  open: () => void
  close: () => void
  createNew: (passkeyName: string) => Promise<void>
  signIn: () => Promise<void>
  clearCreateError: () => void
  clearSignInError: () => void
}

export const useWebAuthnUIStore = create<State & Actions>()((set, _get) => ({
  isOpen: false,
  isCreating: false,
  isSigningIn: false,
  createError: null,
  signInError: null,

  open: () => set({ isOpen: true, createError: null, signInError: null }),
  close: () => set({ isOpen: false, createError: null, signInError: null }),
  clearCreateError: () => set({ createError: null }),
  clearSignInError: () => set({ signInError: null }),

  createNew: async (passkeyName) => {
    set({ isCreating: true, createError: null })
    trackEvent("passkey_create_started")
    try {
      await useWebAuthnStore.getState().createNew(passkeyName)
      useLastConnectedWalletStore
        .getState()
        .recordLastConnectedWallet({ chain: "webauthn", walletId: "passkey" })
      set({ isOpen: false })
      trackEvent("passkey_created")
      trackEvent("account_created", { auth_method: "passkey" })
    } catch (error) {
      const errorMessage = toError(error).message
      const failureType = classifyPasskeyLoginFailure(error)
      set({ createError: errorMessage })
      trackEvent("passkey_creation_failed", {
        failure_type: failureType,
        error_type: errorMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH),
      })
      if (isActionablePasskeyFailure(failureType)) {
        logger.error("Passkey creation failed", {
          error,
          failure_type: failureType,
        })
      }
    } finally {
      set({ isCreating: false })
    }
  },

  signIn: async () => {
    set({ isSigningIn: true, signInError: null })
    trackEvent("passkey_login_started")
    try {
      await useWebAuthnStore.getState().signIn()
      useLastConnectedWalletStore
        .getState()
        .recordLastConnectedWallet({ chain: "webauthn", walletId: "passkey" })
      set({ isOpen: false })
      trackEvent("passkey_login_success")
    } catch (error) {
      const rawErrorMessage = toError(error).message
      const failureType = classifyPasskeyLoginFailure(error)
      set({
        signInError: getPasskeyLoginErrorMessage(failureType, rawErrorMessage),
      })
      trackEvent("passkey_login_failed", {
        failure_type: failureType,
        // Keep raw error_type temporarily for backward-compatible Mixpanel filtering.
        error_type:
          failureType === "credential_not_found"
            ? "credential_not_found"
            : rawErrorMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH),
      })
      if (isActionablePasskeyFailure(failureType)) {
        logger.error("Passkey login failed", {
          error,
          failure_type: failureType,
        })
      }
    } finally {
      set({ isSigningIn: false })
    }
  },
}))
